"use client";

import dynamic from "next/dynamic";
import type { ReactNode } from "react";
import { useAuth } from "@/lib/auth-context";
import VerifyEmailGate from "@/components/VerifyEmailGate";

// The live chat app. Deferred so its bundle never loads for anonymous
// visitors / crawlers - they get the server-rendered SEO landing.
const ChatPage = dynamic(() => import("@/components/chat/ChatPage"), {
  ssr: false,
  loading: () => <div className="fixed inset-0 bg-gray-50 animate-pulse" />,
});

/**
 * Signed-in users get the live chat; everyone else - including Googlebot
 * - gets the server-rendered SEO landing passed as `children`, which is
 * what renders in the SSR HTML.
 */
export default function ChatExperience({ children, bot = false }: { children: ReactNode; bot?: boolean }) {
  const { user, loading } = useAuth();
  // A crawler never has a session: the landing, with no loading shell.
  if (bot) return <>{children}</>;
  // While the session cookie is still being checked, paint a neutral
  // surface instead of the signed-out landing: a signed-in visitor used
  // to see the marketing version flash for a second on every reload.
  if (loading) return <div className="min-h-screen bg-gray-50" aria-busy="true" />;
  if (!user) return <>{children}</>;
  if (user.email_verified === false) return <VerifyEmailGate />;
  return <ChatPage />;
}
